import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../../App';
import { Building2, Calendar, Users, Clock, AlertTriangle, TrendingUp, Bell, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { parseLocalDate, formatTimeRange } from '../../utils/dateTime';
import DonationCard from '../../components/DonationCard';

const PartnerDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    opportunitiesPosted: 0,
    upcomingShifts: 0,
    filledSlots: 0,
    totalSlots: 0,
    hoursCompleted: 0,
    noShows: 0,
    totalSignups: 0
  });
  const [timeframe, setTimeframe] = useState('30'); // 7, 30, or 'month'
  const [loading, setLoading] = useState(true);
  const [orgSuspended, setOrgSuspended] = useState(false);
  const [notifPref, setNotifPref] = useState(user?.notification_preference || 'instant');
  const [savingPref, setSavingPref] = useState(false);
  const [pendingSignups, setPendingSignups] = useState([]);
  const [donationInfo, setDonationInfo] = useState({
    donation_url: null,
    donation_qr_image: null,
    suggested_amounts: [],
    is_available: false
  });

  const fetchDonationInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/donation/partner`);
      setDonationInfo(response.data || { is_available: false });
    } catch (err) {
      console.error('fetchDonationInfo failed', err);
    }
  }, []);

  const fetchPendingSignups = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/signups/pending`);
      setPendingSignups(response.data || []);
    } catch (err) {
      console.error('fetchPendingSignups failed', err);
    }
  }, []);

  const handleApproval = async (signupId, status, reason) => {
    try {
      await axios.put(`${API}/signups/${signupId}/approve`, {
        status,
        declined_reason: reason || null
      });
      toast.success(`Signup ${status === 'approved' ? 'approved' : 'declined'}`);
      fetchPendingSignups();
      fetchStats();
    } catch {
      toast.error('Failed to update signup');
    }
  };

  const fetchStats = useCallback(async () => {
    try {
      // Get all opportunities for this partner org
      const oppsResponse = await axios.get(`${API}/opportunities`);
      const opportunities = oppsResponse.data || [];
      
      // Get all signups for these opportunities
      const signupsResponse = await axios.get(`${API}/admin/signups`);
      const allSignups = signupsResponse.data || [];
      
      // Filter by timeframe
      const now = new Date();
      const filteredOpps = opportunities.filter(opp => {
        const oppDate = parseLocalDate(opp.date);
        if (timeframe === '7') {
          return oppDate >= now && oppDate <= new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        } else if (timeframe === '30') {
          return oppDate >= now && oppDate <= new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        } else {
          // This month
          return oppDate.getMonth() === now.getMonth() && oppDate.getFullYear() === now.getFullYear();
        }
      });
      
      // Calculate stats
      const upcomingOpps = opportunities.filter(opp => parseLocalDate(opp.date) >= now);
      const totalSlots = filteredOpps.reduce((sum, opp) => sum + (opp.capacity || 0), 0);
      const filledSlots = filteredOpps.reduce((sum, opp) => sum + (opp.signups_count || 0), 0);
      
      // Calculate completed hours (attended signups)
      const attendedSignups = allSignups.filter(s => s.status === 'attended');
      const hoursCompleted = attendedSignups.reduce((sum, s) => sum + (s.hours_credited || 0), 0);
      
      // Calculate no-shows
      const noShows = allSignups.filter(s => s.status === 'no_show').length;
      const totalSignups = allSignups.length;
      
      setStats({
        opportunitiesPosted: opportunities.length,
        upcomingShifts: upcomingOpps.length,
        filledSlots,
        totalSlots,
        hoursCompleted,
        noShows,
        totalSignups
      });
    } catch (err) {
      console.error('fetchStats failed', err);
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    fetchStats();
    fetchDonationInfo();
    fetchPendingSignups();
  }, [fetchStats, fetchDonationInfo, fetchPendingSignups]);

  const noShowPercentage = stats.totalSignups > 0 ? ((stats.noShows / stats.totalSignups) * 100).toFixed(1) : 0;

  return (
    <div className="min-h-screen bg-[#FBF4EA]">
      {/* Navigation */}
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div>
                <div className="font-bold text-lg text-[#222222]">{user?.partner_org_name || 'Partner Portal'}</div>
                <div className="text-xs text-[#666666]">{user?.role === 'partner_admin' ? 'Admin' : 'Staff'}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/partner/dashboard" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Participants</Link>
              <Link to="/partner/invites" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Invites</Link>
              <Link to="/partner/staff" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222222] hover:text-[#8B2E3C] font-medium">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline text-sm">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Suspended Banner */}
      {orgSuspended && (
        <div className="bg-red-100 border-b-2 border-red-300 py-3">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-700" />
            <span className="text-red-800 font-medium">Organization suspended — contact admin. View-only mode.</span>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#222222] mb-3">Partner Dashboard</h1>
          <p className="text-base text-[#666666]">Overview of your organization's volunteer program</p>
        </div>

        {/* Timeframe Selector */}
        <div className="mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setTimeframe('7')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeframe === '7' ? 'bg-[#8B2E3C] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
              }`}
            >
              Next 7 Days
            </button>
            <button
              onClick={() => setTimeframe('30')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeframe === '30' ? 'bg-[#8B2E3C] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
              }`}
            >
              Next 30 Days
            </button>
            <button
              onClick={() => setTimeframe('month')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                timeframe === 'month' ? 'bg-[#8B2E3C] text-white' : 'bg-white border-2 border-[#E9DDCF] text-[#222222]'
              }`}
            >
              This Month
            </button>
          </div>
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Opportunities Posted */}
              <div className="card">
                <div className="flex items-start justify-between mb-3">
                  <Calendar className="w-8 h-8 text-[#8B2E3C]" />
                </div>
                <div className="text-3xl font-bold text-[#222222] mb-1">{stats.opportunitiesPosted}</div>
                <div className="text-sm text-[#666666]">Opportunities Posted</div>
              </div>

              {/* Upcoming Shifts */}
              <div className="card">
                <div className="flex items-start justify-between mb-3">
                  <Clock className="w-8 h-8 text-[#C9A227]" />
                </div>
                <div className="text-3xl font-bold text-[#222222] mb-1">{stats.upcomingShifts}</div>
                <div className="text-sm text-[#666666]">Upcoming Shifts</div>
              </div>

              {/* Filled vs Open Slots */}
              <div className="card">
                <div className="flex items-start justify-between mb-3">
                  <Users className="w-8 h-8 text-[#8B2E3C]" />
                </div>
                <div className="text-3xl font-bold text-[#222222] mb-1">
                  {stats.filledSlots} / {stats.totalSlots}
                </div>
                <div className="text-sm text-[#666666]">Filled / Total Slots</div>
                <div className="mt-2">
                  <div className="w-full bg-[#E9DDCF] rounded-full h-2">
                    <div 
                      className="bg-[#8B2E3C] h-2 rounded-full" 
                      style={{width: `${stats.totalSlots > 0 ? (stats.filledSlots / stats.totalSlots * 100) : 0}%`}}
                    />
                  </div>
                </div>
              </div>

              {/* Hours Completed */}
              <div className="card">
                <div className="flex items-start justify-between mb-3">
                  <TrendingUp className="w-8 h-8 text-[#8B2E3C]" />
                </div>
                <div className="text-3xl font-bold text-[#222222] mb-1">{stats.hoursCompleted.toFixed(1)}</div>
                <div className="text-sm text-[#666666]">Hours Completed</div>
              </div>

              {/* No-Shows */}
              <div className="card">
                <div className="flex items-start justify-between mb-3">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div className="text-3xl font-bold text-[#222222] mb-1">
                  {stats.noShows} <span className="text-lg">({noShowPercentage}%)</span>
                </div>
                <div className="text-sm text-[#666666]">No-Shows</div>
              </div>
            </div>

            {/* Pending Approvals */}
            {pendingSignups.length > 0 && (
              <div className="card mb-8" data-testid="pending-approvals-section">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-[#C9A227]" />
                    <h3 className="text-lg font-bold text-[#222222]">Pending Approvals</h3>
                    <span className="bg-[#C9A227] text-white text-xs font-bold px-2 py-0.5 rounded-full">{pendingSignups.length}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  {pendingSignups.map(signup => (
                    <div key={signup.id} className="flex items-center justify-between p-3 bg-[#FBF4EA] rounded-lg border border-[#E9DDCF]" data-testid={`pending-signup-${signup.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#222222] text-sm truncate">{signup.user_name || signup.user_email}</div>
                        <div className="text-xs text-[#666666] truncate">
                          {signup.opportunity_title} — {signup.opportunity_date}{signup.opportunity_start_time ? ` • ${formatTimeRange(signup.opportunity_start_time, signup.opportunity_end_time)}` : ''}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4 shrink-0">
                        <button
                          onClick={() => handleApproval(signup.id, 'approved')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors"
                          data-testid={`approve-btn-${signup.id}`}
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          Approve
                        </button>
                        <button
                          onClick={() => handleApproval(signup.id, 'declined', 'Declined by partner organization')}
                          className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
                          data-testid={`decline-btn-${signup.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Links */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              <Link to="/partner/opportunities" className="card hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-[#222222] mb-2">Manage Opportunities</h3>
                <p className="text-sm text-[#666666]">Create, edit, and manage your volunteer opportunities</p>
              </Link>
              
              <Link to="/partner/time-entries" className="card hover:shadow-lg transition-shadow border-l-4 border-l-[#8B2E3C]">
                <h3 className="text-lg font-bold text-[#222222] mb-2">Time Entry Approval</h3>
                <p className="text-sm text-[#666666]">Review and approve volunteer check-in hours</p>
              </Link>
              
              <Link to="/partner/reports" className="card hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-[#222222] mb-2">View Reports</h3>
                <p className="text-sm text-[#666666]">See detailed reports on hours and attendance</p>
              </Link>
            </div>

            {/* Donation Support Card */}
            <DonationCard
              donationUrl={donationInfo.donation_url}
              donationQrImage={donationInfo.donation_qr_image}
              suggestedAmounts={donationInfo.suggested_amounts || []}
              isAvailable={donationInfo.is_available}
              showTitle={true}
            />

            {/* Notification Preferences */}
            <div className="card mt-6" data-testid="notification-preferences-card">
              <div className="flex items-center gap-2 mb-4">
                <Bell className="w-5 h-5 text-[#8B2E3C]" />
                <h3 className="text-lg font-bold text-[#222]">Email Notification Preferences</h3>
              </div>
              <div className="space-y-2">
                {[
                  { value: 'instant', label: 'Instant', desc: 'Receive an email immediately when a volunteer signs up for your opportunities' },
                  { value: 'daily_digest', label: 'Daily Digest', desc: 'Receive one summary email per day with new signups and upcoming volunteers' },
                  { value: 'none', label: 'None', desc: 'No email notifications for volunteer signups' },
                ].map(opt => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer border-2 transition-colors ${
                      notifPref === opt.value ? 'border-[#8B2E3C] bg-[#FBF4EA]' : 'border-[#E9DDCF] hover:border-[#C9A227]'
                    }`}
                    data-testid={`notif-pref-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name="notif_pref"
                      value={opt.value}
                      checked={notifPref === opt.value}
                      onChange={async () => {
                        setNotifPref(opt.value);
                        setSavingPref(true);
                        try {
                          await axios.put(`${API}/auth/notification-preference`, { notification_preference: opt.value });
                          toast.success(`Notifications set to: ${opt.label}`);
                        } catch (err) {
                          toast.error('Failed to update preference');
                        } finally {
                          setSavingPref(false);
                        }
                      }}
                      className="mt-1 accent-[#8B2E3C]"
                    />
                    <div>
                      <div className="font-medium text-[#222] text-sm">{opt.label}</div>
                      <div className="text-xs text-[#666] mt-0.5">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {savingPref && <p className="text-xs text-[#888] mt-2">Saving...</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PartnerDashboard;
